import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Spinner from 'react-bootstrap/Spinner';
import {
  filterStorageTypeMap,
  TABLE_REFETCH_INTERVAL_MILISEC,
  TrackerDataType,
  visibleServiceLogCols,
} from '../../shared/constants/constants';
import { useServiceLogHandler } from '../../shared/eventHandler/ServiceLogEventHandler';
import { useMediaInteractionHandler } from '../../shared/eventHandler/MediaInteractionEventHandler';
import TrackerTable from '../TrackerTable/TrackerTable';
import { getCurrMonth } from '../../shared/functions/dateProcessor';
import { useLocation, useParams } from 'react-router-dom';
import { useFilters, useFiltersDispatch } from '../TrackerFilter/Context/FiltersContext';
import isFilterEmpty from '../../shared/functions/isFilterEmpty';
import MonthSelector from '../TrackerTable/MonthSelector';
import TrackerTableFilter from '../TrackerTable/TrackerTableFilter';
import TrackerTableFuzzySearch from '../TrackerTable/TrackerTableFuzzySearch';
import dayjs from '../../utils/dayjs';
import { useEngagementLogHandler } from '../../shared/eventHandler/EngagementLogEventHandler';
import { TrackerTableActionButtons } from '../TrackerTable/TrackerTableActionButtons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getFilteredServices, getMonthlyServices } from '../../shared/requests/serviceNetworkRequests';
import { useRecentCreations } from '../../shared/hooks/useRecentCreations';
import { getTrackerFilterQueryFromFilters } from '../TrackerFilter/trackerFilterHelpers';

const useStyles = makeStyles({
  tableContainer: {
    width: 'calc(100% - 60px)',
    margin: '0 auto',
    marginBottom: 30,
  },
  filterContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '1rem',
    marginBottom: '1rem',
    height: '32px',
  },
  filterWrapper: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
  },
  actionButtonsWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    gap: '1rem',
  },
});

export const ServiceLogPage = ({ dropdownTable, user }) => {
  const classes = useStyles();
  const filtersDispatch = useFiltersDispatch();
  const { state } = useLocation();
  const recentCreations = useRecentCreations();
  const { markCreated } = recentCreations;
  const previousMonthlyIds = useRef(null);

  let month;
  if (sessionStorage.getItem('currMonth')) {
    month = sessionStorage.getItem('currMonth');
  } else {
    month = getCurrMonth();
  }

  const [data, setData] = useState([]);
  const [currMonth, setMonth] = useState(month);
  const [isBulkEdit, setIsBulkEdit] = useState(false);
  const [selectedBulkEditRowIds, setSelectedBulkEditRowIds] = useState([]);

  const existingFilters = useFilters();

  const queryClient = useQueryClient();
  const onSuccessCallback = async () => {
    await queryClient.invalidateQueries({ queryKey: ['filteredServices'] });
    await queryClient.invalidateQueries({ queryKey: ['monthlyServices'] });
  };
  const serviceLogEventHandler = useServiceLogHandler({
    currMonth,
    recentCreations,
    onSuccessCallback,
  });
  const mediaInteractionEventHandler = useMediaInteractionHandler({
    currMonth,
    recentCreations,
    onSuccessCallback,
  });
  const engagementLogEventHandler = useEngagementLogHandler({
    currMonth,
    recentCreations,
    onSuccessCallback,
  });

  const serviceParams = useParams();

  const { data: monthlyServices, refetch: refetchMontlyServices } = useQuery({
    queryKey: ['monthlyServices', currMonth],
    queryFn: () => getMonthlyServices(currMonth, dayjs.tz.guess()),
    enabled: !state && isFilterEmpty(existingFilters),
    refetchInterval: TABLE_REFETCH_INTERVAL_MILISEC,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const filtersQueryString = useMemo(
    () => getTrackerFilterQueryFromFilters(existingFilters, TrackerDataType.SERVICE),
    [existingFilters]
  );

  const { data: filteredServices } = useQuery({
    queryKey: ['filteredServices', filtersQueryString],
    queryFn: async () => {
      const data = await getFilteredServices(filtersQueryString);
      return data || [];
    },
    enabled: !!filtersQueryString,
  });

  const shouldDisableMonthSwitches = () => {
    const hasFilters = Object.keys(existingFilters).length > 0;
    const isQuickSearching = state?.queryString && state?.category;
    return hasFilters || isQuickSearching;
  };

  const handleBulkEditSelectAll = (isChecked, pageRowIds) => {
    if (isChecked) {
      setSelectedBulkEditRowIds(pageRowIds);
    } else {
      setSelectedBulkEditRowIds([]);
    }
  };

  const handleBulkEditSelectRow = (isChecked, rowId) => {
    setSelectedBulkEditRowIds((prevSelectedIds) => {
      if (isChecked) {
        return [...prevSelectedIds, rowId];
      } else {
        return prevSelectedIds.filter((id) => rowId !== id);
      }
    });
  };

  // Filter service based on url
  useEffect(() => {
    if (serviceParams.serviceId) {
      filtersDispatch({
        type: 'setFilters',
        payload: {
          id: serviceParams.serviceId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceParams]);

  useEffect(() => {
    if (!monthlyServices && !filteredServices) {
      return;
    }
    const storedFilter = user.nonPIUserSettings?.userStoredFilters?.[filterStorageTypeMap[TrackerDataType.SERVICE]];
    if (!isFilterEmpty(existingFilters) && filteredServices) {
      setData(filteredServices);
    } else if (isFilterEmpty(existingFilters) && !state?.queryString && !storedFilter && monthlyServices) {
      setData(monthlyServices);
    }
  }, [
    monthlyServices,
    filteredServices,
    existingFilters,
    state,
    user.nonPIUserSettings?.userStoredFilters,
    filtersQueryString,
  ]);

  useEffect(() => {
    previousMonthlyIds.current = null;
  }, [currMonth]);

  useEffect(() => {
    if (!Array.isArray(monthlyServices)) {
      return;
    }
    const storedFilter = user.nonPIUserSettings?.userStoredFilters?.[filterStorageTypeMap[TrackerDataType.SERVICE]];
    const shouldTrack = isFilterEmpty(existingFilters) && !state?.queryString && !storedFilter;
    const nextIds = new Set(monthlyServices.map((row) => row.id));

    if (shouldTrack && previousMonthlyIds.current) {
      const prevIds = previousMonthlyIds.current;
      monthlyServices.forEach((row) => {
        if (!prevIds.has(row.id)) {
          markCreated(row.id);
        }
      });
    }

    previousMonthlyIds.current = nextIds;
  }, [monthlyServices, existingFilters, state, user.nonPIUserSettings?.userStoredFilters, markCreated]);

  useEffect(() => {
    sessionStorage.setItem('currMonth', currMonth);
    if (!state) {
      if (isFilterEmpty(existingFilters)) {
        refetchMontlyServices();
      }
    }
  }, [currMonth, existingFilters, refetchMontlyServices, state]);

  return (
    <Box className={classes.tableContainer}>
      <Box>
        <MonthSelector disabled={shouldDisableMonthSwitches()} currMonth={currMonth} onMonthChange={setMonth} />
      </Box>
      <Box className={classes.filterContainer}>
        <Box className={classes.filterWrapper}>
          <TrackerTableFuzzySearch />
          <TrackerTableFilter
            setDataCallback={setData}
            eventHandler={serviceLogEventHandler}
            dropdownTable={dropdownTable}
          />
        </Box>
        <Box className={classes.actionButtonsWrapper}>
          <TrackerTableActionButtons
            eventHandler={serviceLogEventHandler}
            dropdownTable={dropdownTable}
            currMonth={currMonth}
            rows={data}
            isBulkEdit={isBulkEdit}
            selectedBulkEditRowIds={selectedBulkEditRowIds}
            selectedBulkEditRowsCount={selectedBulkEditRowIds.length}
            onSwitchToBulkEditView={() => {
              setIsBulkEdit(true);
            }}
            onSwitchToMonthlyView={() => {
              setIsBulkEdit(false);
              setSelectedBulkEditRowIds([]);
            }}
          />
        </Box>
      </Box>
      {data && dropdownTable ? (
        <>
          <TrackerTable
            rows={data}
            dropdownTable={dropdownTable}
            visibleCols={visibleServiceLogCols}
            eventHandler={serviceLogEventHandler}
            linkedEventHandler={mediaInteractionEventHandler}
            secondaryLinkedEventHandler={engagementLogEventHandler}
            user={user}
            isBulkEdit={isBulkEdit}
            selectedBulkEditRowIds={selectedBulkEditRowIds}
            onBulkEditSelectAll={handleBulkEditSelectAll}
            onBulkEditSelectRow={handleBulkEditSelectRow}
          />
        </>
      ) : (
        <div className="spinner-container">
          <Spinner animation="border" />
        </div>
      )}
    </Box>
  );
};
